/**
 * Wrapper pour intégrer l'interface d'avantages en nature dans l'application
 * Gère l'état global, l'authentification et les permissions
 */

import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useSelection } from "../../SelectionContext";
import {
  Layout,
  Card,
  Button,
  Spin,
  Result,
  Row,
  Col,
  Typography,
  Statistic,
  Avatar,
  Tag,
  Space,
  Divider,
  Alert,
} from "antd";
import {
  UserOutlined,
  BankOutlined,
  TeamOutlined,
  LeftOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import NaturaBenefitsManager from "../../components/hr/NaturaBenefitsManager";
import { getAuthorizedCompanyContext } from "../../utils/companyContext.js";

const { Content } = Layout;
const { Title, Text } = Typography;

interface Employee {
  id: number;
  firstName: string;
  lastName: string;
  employeeNumber: string;
  basicSalary: number;
  sector: "public" | "private";
  dependentsCount: number;
  professionalCategory: string;
  department: string;
  position: string;
  isActive: boolean;
}

const NaturaBenefitsWrapper: React.FC = () => {
  const { user } = useAuth();
  const { selectedCompany } = useSelection();
  const rawSelectedCompanyId =
    typeof selectedCompany === "object" && selectedCompany !== null
      ? selectedCompany.id
      : selectedCompany;
  const parsedPrimaryCompanyId = Number(user?.company_id);
  const authorizedCompanyId = getAuthorizedCompanyContext();
  const companyId =
    authorizedCompanyId ||
    (Number.isInteger(parsedPrimaryCompanyId) && parsedPrimaryCompanyId > 0
      ? parsedPrimaryCompanyId
      : null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadEmployees = async () => {
    setLoading(true);
    setError(null);

    try {
      if (!companyId) {
        setEmployees([]);
        throw new Error("Aucune entreprise active n'est disponible");
      }

      // Utiliser le même token que le système RH existant
      const token = localStorage.getItem("authToken"); // Utiliser 'authToken' comme dans useHRAPI

      const response = await fetch(
        `/api/hr/employees?company_id=${encodeURIComponent(String(companyId))}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Erreur lors du chargement des employés");
      }

      const data = await response.json();

      if (data.success) {
        // Transformation des données pour l'interface avec calcul du salaire depuis les éléments de paie uniquement
        const transformedEmployees = await Promise.all(
          data.data.map(async (emp: any) => {
            let basicSalary = 0;

            try {
              // Récupérer le salaire uniquement depuis les éléments de paie
              const payrollResponse = await fetch(
                `/api/hr/payroll-items/employee/${emp.id}?company_id=${encodeURIComponent(
                  String(companyId)
                )}`,
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                  },
                }
              );

              if (payrollResponse.ok) {
                const payrollData = await payrollResponse.json();
                if (payrollData.success && payrollData.data?.length > 0) {
                  // Rechercher l'élément de salaire de base dans les éléments de paie
                  // Priorité 1: Code exact du système
                  let baseSalaryItem = payrollData.data.find(
                    (item: any) => item.code === "SAL_BASE"
                  );

                  // Priorité 2: Catégorie salary et type gain
                  if (!baseSalaryItem) {
                    baseSalaryItem = payrollData.data.find(
                      (item: any) =>
                        item.category === "salary" && item.type === "gain"
                    );
                  }

                  // Priorité 3: Recherche étendue par nom et catégorie
                  if (!baseSalaryItem) {
                    baseSalaryItem = payrollData.data.find(
                      (item: any) =>
                        item.type === "gain" &&
                        // Chercher par nom contenant salaire/base
                        (item.name?.toLowerCase().includes("salaire") ||
                          item.name?.toLowerCase().includes("base") ||
                          // Chercher par catégorie salaire
                          item.category?.toLowerCase().includes("salaire") ||
                          item.category?.toLowerCase().includes("salary") ||
                          // Codes alternatifs
                          item.code === "SALAIRE_BASE" ||
                          item.code === "SALARY" ||
                          item.code === "BASE_SALARY")
                    );
                  }

                  if (baseSalaryItem) {
                    // Essayer plusieurs champs pour le montant selon la structure des données
                    const amount =
                      baseSalaryItem.custom_amount ||
                      baseSalaryItem.calculated_value ||
                      baseSalaryItem.default_amount ||
                      baseSalaryItem.base_amount ||
                      baseSalaryItem.amount;

                    if (amount && amount > 0) {
                      basicSalary = Number(amount);
                    } else {
                      console.warn(
                        `⚠️ Salary item found but no valid amount for ${emp.first_name} ${emp.last_name}`,
                        {
                          code: baseSalaryItem.code,
                          name: baseSalaryItem.name,
                          custom_amount: baseSalaryItem.custom_amount,
                          default_amount: baseSalaryItem.default_amount,
                          base_amount: baseSalaryItem.base_amount,
                        }
                      );
                    }
                  } else {
                    console.warn(
                      `❌ No basic salary item found for ${emp.first_name} ${emp.last_name}`,
                      {
                        available_items: payrollData.data.length,
                        item_codes: payrollData.data.map(
                          (item: any) =>
                            `${item.code} (${item.type}, ${item.category})`
                        ),
                        note: "Employee may need a 'SAL_BASE' payroll item assigned",
                      }
                    );
                  }
                }
              }

              if (basicSalary === 0) {
                console.warn(
                  `💰 No valid salary amount found for employee ${emp.first_name} ${emp.last_name} (ID: ${emp.id}). Please ensure a basic salary payroll item is assigned.`
                );
              }
            } catch (salaryError) {
              console.error(
                `Error fetching payroll items for employee ${emp.id}:`,
                salaryError
              );
            }

            return {
              id: emp.id,
              firstName: emp.first_name,
              lastName: emp.last_name,
              employeeNumber: emp.employee_number,
              basicSalary: basicSalary,
              sector: emp.sector || "private",
              dependentsCount: emp.dependents_count || 0,
              professionalCategory: emp.professional_category || "non_cadre",
              department: emp.department_name || "Non défini",
              position: emp.position_title || "Non définie",
              isActive: emp.status === "active",
            };
          })
        );

        const activeEmployees = transformedEmployees.filter(
          (emp: Employee) => emp.isActive
        );

        setEmployees(activeEmployees);
      } else {
        throw new Error(data.message || "Erreur lors du chargement");
      }
    } catch (error) {
      console.error("Erreur chargement employés:", error);
      setError(error instanceof Error ? error.message : "Erreur inconnue");
    } finally {
      setLoading(false);
    }
  };

  // Recharger la liste lorsque l'utilisateur change d'entreprise active.
  useEffect(() => {
    setSelectedEmployee(null);
    loadEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Sélection d'un employé
  const handleEmployeeSelect = (employee: Employee) => {
    setSelectedEmployee(employee);
  };

  // Retour à la liste
  const handleBackToList = () => {
    setSelectedEmployee(null);
  };

  // Rendu de la liste des employés
  const renderEmployeesList = () => (
    <Layout>
      <Content style={{ padding: "24px" }}>
        {/* En-tête */}
        <Card className="mb-6" bordered={false}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <Title level={2} style={{ marginBottom: 8 }}>
                Avantages en Nature
              </Title>
              <Text type="secondary">
                Gestion des avantages en nature selon la réglementation
                burkinabè
              </Text>
              <div style={{ marginTop: 8 }}>
                <Space size="large">
                  <Space>
                    <BankOutlined />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Conforme arrêté 2023-0017/MFPTPS/SG/DGPS
                    </Text>
                  </Space>
                  <Space>
                    <TeamOutlined />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {employees.length} employé(s) actif(s)
                    </Text>
                  </Space>
                </Space>
              </div>
            </div>
          </div>
        </Card>

        {/* Statistiques rapides */}
        <Row gutter={[16, 16]} className="mb-6">
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Employés actifs"
                value={employees.length}
                valueStyle={{ color: "#1890ff" }}
                prefix={<TeamOutlined />}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Secteur public"
                value={
                  employees.filter((emp) => emp.sector === "public").length
                }
                valueStyle={{ color: "#52c41a" }}
                prefix={<BankOutlined />}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Secteur privé"
                value={
                  employees.filter((emp) => emp.sector === "private").length
                }
                valueStyle={{ color: "#722ed1" }}
                prefix={<UserOutlined />}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Salaire moyen (FCFA)"
                value={
                  Math.round(
                    employees.reduce((sum, emp) => sum + emp.basicSalary, 0) /
                      employees.length
                  ) || 0
                }
                valueStyle={{ color: "#fa8c16" }}
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
              />
            </Card>
          </Col>
        </Row>

        {/* Liste des employés */}
        <Card title="Sélectionner un employé">
          <Row gutter={[16, 16]}>
            {employees.map((employee) => (
              <Col xs={24} sm={12} lg={8} key={employee.id}>
                <Card
                  hoverable
                  onClick={() => handleEmployeeSelect(employee)}
                  style={{
                    cursor: "pointer",
                    border: "1px solid #d9d9d9",
                    transition: "all 0.3s",
                  }}
                >
                  <div style={{ padding: "8px 0" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: 8,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <Avatar icon={<UserOutlined />} />
                        <div>
                          <Title level={5} style={{ margin: 0 }}>
                            {employee.firstName} {employee.lastName}
                          </Title>
                        </div>
                      </div>
                      <Tag
                        color={employee.sector === "public" ? "blue" : "green"}
                      >
                        {employee.sector === "public" ? "Public" : "Privé"}
                      </Tag>
                    </div>

                    <div style={{ marginBottom: 8 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        N° {employee.employeeNumber}
                      </Text>
                    </div>

                    <div style={{ marginBottom: 8 }}>
                      <Text style={{ fontSize: 13 }}>
                        {employee.department} • {employee.position}
                      </Text>
                    </div>

                    <div style={{ marginBottom: 8 }}>
                      <Text strong>
                        Salaire: {employee.basicSalary.toLocaleString()} FCFA
                      </Text>
                    </div>

                    {employee.dependentsCount > 0 && (
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {employee.dependentsCount} charge(s) familiale(s)
                        </Text>
                      </div>
                    )}
                  </div>
                </Card>
              </Col>
            ))}
          </Row>

          {employees.length === 0 && !loading && (
            <Result
              icon={<TeamOutlined />}
              title="Aucun employé actif trouvé"
              subTitle="Vérifiez que des employés sont configurés dans le système"
            />
          )}
        </Card>
      </Content>
    </Layout>
  );

  // Rendu des avantages en nature pour l'employé sélectionné
  const renderNaturaBenefitsInterface = () => {
    if (!selectedEmployee) return null;

    // Vérification de sécurité - s'assurer qu'on a un company_id valide
    if (!companyId) {
      console.error(
        "❌ Aucun company_id disponible pour les avantages nature",
        {
          user_company_id: user?.company_id,
          selectedCompany_id: rawSelectedCompanyId,
          user_object: user,
        }
      );
      return (
        <Layout>
          <Content style={{ padding: "24px" }}>
            <Alert
              message="Erreur de configuration"
              description="Impossible de déterminer l'entreprise associée. Veuillez vous reconnecter ou contacter l'administrateur."
              type="error"
              showIcon
              action={
                <Button size="small" onClick={() => window.location.reload()}>
                  Actualiser la page
                </Button>
              }
            />
          </Content>
        </Layout>
      );
    }

    return (
      <Layout>
        <Content style={{ padding: "24px" }}>
          {/* Navigation retour */}
          <Card className="mb-6" bordered={false}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <Button
                icon={<LeftOutlined />}
                onClick={handleBackToList}
                type="text"
                style={{ fontSize: 16 }}
              >
                Retour à la liste
              </Button>

              <Divider type="vertical" style={{ height: 40 }} />

              <div style={{ flex: 1 }}>
                <Title level={2} style={{ margin: 0 }}>
                  Avantages en nature - {selectedEmployee.firstName}{" "}
                  {selectedEmployee.lastName}
                </Title>
                <div style={{ marginTop: 8 }}>
                  <Space size="middle">
                    <Text type="secondary">
                      N° {selectedEmployee.employeeNumber}
                    </Text>
                    <Text type="secondary">•</Text>
                    <Text type="secondary">{selectedEmployee.department}</Text>
                    <Text type="secondary">•</Text>
                    <Text type="secondary">{selectedEmployee.position}</Text>
                    <Text type="secondary">•</Text>
                    <Tag
                      color={
                        selectedEmployee.sector === "public" ? "blue" : "green"
                      }
                    >
                      Secteur{" "}
                      {selectedEmployee.sector === "public"
                        ? "Public"
                        : "Privé"}
                    </Tag>
                  </Space>
                </div>
              </div>
            </div>
          </Card>

          {/* Interface principale des avantages en nature */}
          <NaturaBenefitsManager
            employeeId={selectedEmployee.id}
            companyId={companyId}
            employeeData={{
              id: selectedEmployee.id,
              firstName: selectedEmployee.firstName,
              lastName: selectedEmployee.lastName,
              basicSalary: selectedEmployee.basicSalary,
              sector: selectedEmployee.sector,
            }}
          />
        </Content>
      </Layout>
    );
  };

  // Rendu principal
  if (loading) {
    return (
      <Layout>
        <Content style={{ padding: "24px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "60vh",
              flexDirection: "column",
            }}
          >
            <Spin size="large" />
            <Text style={{ marginTop: 16 }}>Chargement des employés...</Text>
          </div>
        </Content>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Content style={{ padding: "24px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "60vh",
            }}
          >
            <Result
              status="error"
              title="Erreur de chargement"
              subTitle={error}
              extra={
                <Button type="primary" onClick={loadEmployees}>
                  Réessayer
                </Button>
              }
            />
          </div>
        </Content>
      </Layout>
    );
  }

  return selectedEmployee
    ? renderNaturaBenefitsInterface()
    : renderEmployeesList();
};

export default NaturaBenefitsWrapper;
